import { useState, useEffect } from 'react'
import {
  Trash2,
  HardDrive,
  ShieldCheck,
  RefreshCw,
  UserRound,
} from 'lucide-react'
import { useAppStore } from '../store'
import { getCacheInfo, clearCache } from '../lib/cache'
import { ConfirmDialog, ConfirmOptions } from '../components/ui/ConfirmDialog'
import { toast } from '../store/toast'

const SECURITY = [
  {
    title: 'End-to-end encryption',
    body: 'All data is encrypted with AES-256-GCM before leaving your device.',
  },
  {
    title: 'Zero-knowledge architecture',
    body: 'Servers never see your passphrase or decrypted content.',
  },
  {
    title: 'Memory-hard key derivation',
    body: 'Your key is derived with a memory-hard function that resists brute-force attacks.',
  },
  {
    title: 'Cloudflare Access protection',
    body: 'An additional authentication layer before you reach the app.',
  },
]

export function SettingsPage() {
  const userEmail = useAppStore((s) => s.userEmail)

  const [cacheInfo, setCacheInfo] = useState<{
    totalSize: number
    vaultCount: number
    maxSize: number
  } | null>(null)
  const [clearingCache, setClearingCache] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmOptions | null>(null)

  useEffect(() => {
    getCacheInfo().then(setCacheInfo)
  }, [])

  const doClearCache = async () => {
    setClearingCache(true)
    try {
      await clearCache()
      setCacheInfo({
        totalSize: 0,
        vaultCount: 0,
        maxSize: cacheInfo?.maxSize || 0,
      })
      toast('Offline cache cleared', 'success')
    } catch (error) {
      console.error('Failed to clear cache:', error)
      toast('Could not clear the cache', 'error')
    } finally {
      setClearingCache(false)
    }
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  const pct =
    cacheInfo && cacheInfo.maxSize
      ? Math.min((cacheInfo.totalSize / cacheInfo.maxSize) * 100, 100)
      : 0

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-8">
      <div className="mb-6 animate-fade-up">
        <h1 className="font-display text-[30px] font-semibold leading-tight tracking-tight sm:text-[38px]">
          Settings
        </h1>
        <p className="mt-1 text-muted">
          Your account, offline cache and how Vault keeps data safe.
        </p>
      </div>

      <section
        className="panel p-5 sm:p-6 animate-fade-up"
        style={{ animationDelay: '60ms' }}
      >
        <h2 className="mb-4 flex items-center gap-3 font-display font-semibold">
          <span className="kind-ic note" aria-hidden="true">
            <UserRound className="h-5 w-5" />
          </span>
          Account
        </h2>
        <div className="rounded-2xl border border-white/[0.07] bg-black/20 p-4">
          <div className="text-[13px] text-faint">
            Signed in via Cloudflare Access
          </div>
          <div className="mt-0.5 truncate font-semibold">
            {userEmail || 'Loading…'}
          </div>
        </div>
      </section>

      <section
        className="panel p-5 sm:p-6 animate-fade-up"
        style={{ animationDelay: '120ms' }}
      >
        <h2 className="mb-1 flex items-center gap-3 font-display font-semibold">
          <span className="kind-ic bm" aria-hidden="true">
            <HardDrive className="h-5 w-5" />
          </span>
          Offline cache
        </h2>
        <p className="mb-4 text-sm text-muted">
          Vaults are cached on this device, still encrypted, so they open
          without a connection.
        </p>

        {cacheInfo && (
          <div className="mb-4 rounded-2xl border border-white/[0.07] bg-black/20 p-4">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <span className="font-display text-2xl font-semibold">
                {formatBytes(cacheInfo.totalSize)}
              </span>
              <span className="text-[13px] text-faint">
                of {formatBytes(cacheInfo.maxSize)} · {cacheInfo.vaultCount}{' '}
                {cacheInfo.vaultCount === 1 ? 'vault' : 'vaults'}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-vault-400 to-[#3FB8C9] transition-[width] duration-700 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={() =>
            setConfirm({
              title: 'Clear the offline cache?',
              body: 'Cached vaults are removed from this device. You’ll need a connection to open them again.',
              confirmLabel: 'Clear cache',
              danger: true,
              onConfirm: doClearCache,
            })
          }
          disabled={clearingCache || cacheInfo?.vaultCount === 0}
          className="btn-danger w-full"
        >
          {clearingCache ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          {clearingCache ? 'Clearing…' : 'Clear cache'}
        </button>
      </section>

      <section
        className="panel p-5 sm:p-6 animate-fade-up"
        style={{ animationDelay: '180ms' }}
      >
        <h2 className="mb-4 flex items-center gap-3 font-display font-semibold">
          <span className="kind-ic note" aria-hidden="true">
            <ShieldCheck className="h-5 w-5" />
          </span>
          Security
        </h2>
        <ol className="space-y-3">
          {SECURITY.map((s, i) => (
            <li key={s.title} className="flex items-start gap-3">
              <span className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-full bg-white/[0.08] text-xs font-bold text-vault-300">
                {i + 1}
              </span>
              <div>
                <div className="font-semibold">{s.title}</div>
                <div className="text-sm text-muted">{s.body}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <p className="pt-2 text-center text-sm text-faint">Vault v1.0.0</p>

      {confirm && (
        <ConfirmDialog options={confirm} onClose={() => setConfirm(null)} />
      )}
    </div>
  )
}
